/**
 * Resolve an external host-platform URL for a booking (Airbnb / VRBO / stored reservation_url).
 */

export type BookingPlatformLink = {
  url: string;
  /** Short label for tooltips, e.g. "Airbnb", "VRBO". */
  platformLabel: string;
};

function looksLikeAirbnbConfirmation(ref: string): boolean {
  return /^[A-Z0-9]{8,14}$/.test(ref);
}

/**
 * Prefer stored reservation_url; for Airbnb also build host details URL from booking ref.
 * VRBO has no reliable code→URL pattern here — only an explicit reservation_url works.
 */
export function resolveBookingPlatformLink(booking: {
  platform?: string | null;
  reservation_url?: string | null;
  booking_ref?: string | null;
}): BookingPlatformLink | null {
  const url = String(booking.reservation_url || '').trim();
  const platform = String(booking.platform || '').trim().toLowerCase();

  if (url) {
    if (/airbnb\.com/i.test(url)) {
      return { url, platformLabel: 'Airbnb' };
    }
    if (/vrbo\.com|homeaway\.com/i.test(url)) {
      return { url, platformLabel: 'VRBO' };
    }
    if (platform.includes('airbnb')) return { url, platformLabel: 'Airbnb' };
    if (platform.includes('vrbo') || platform.includes('homeaway')) {
      return { url, platformLabel: 'VRBO' };
    }
    return { url, platformLabel: 'Platform' };
  }

  if (platform && !platform.includes('airbnb') && platform !== '') {
    // No URL and not Airbnb — cannot invent a VRBO deep link from ref alone.
    return null;
  }

  const ref = String(booking.booking_ref || '').trim().toUpperCase();
  if (looksLikeAirbnbConfirmation(ref) && (!platform || platform.includes('airbnb'))) {
    return {
      url: `https://www.airbnb.com/hosting/reservations/details/${ref}`,
      platformLabel: 'Airbnb',
    };
  }

  return null;
}
